'use client'

import { AVERAGE_SLOT_TIME, COLLECTIONS_REVALIDATE_TAG } from '@/app/constants/common'
import { ProjectTokenGridItem } from './project-token-grid-item'
import { useEffect, useMemo, useState } from 'react'
import { useTokenFilters } from './hooks/use-token-filters'
import { getActiveFilterCount } from '@utils/token-features'
import {
  GetProjectTokensDocument,
  GetProjectTokensQueryVariables,
  ProjectDetailFragment
} from '@/gql/generated/graphql'
import { Grid, GridType } from '@components/grid'
import { ProjectTokensFilter } from './tokens-filter'
import { OperationContext, useQuery } from '@urql/next'
import { Button, buttonVariants } from '@components/button'
import React from 'react'
import { createUrqlNextCacheContext, createTagWithVal } from '@utils/common'
import { useLocalStorage } from '@/hooks/use-local-storage'
import { BodyText } from 'design-configuration/components/responsive-text'
import { tokenFilterParsers } from './utils'
import { useQueryStates } from 'nuqs'
import { AllPurposeLink } from '@components/all-purpose-link'
import { gridExperimentRoute } from '@utils/routes'
import { cn } from '@/lib/utils'
import { TrackedButtonNames } from '@utils/analytics'
import { trackButtonClicked } from '@utils/analytics'

enum ProjectGrids {
  DEFAULT = 'default',
  SEARCH = 'search',
  NONE = ''
}

type ProjectWorksProps = {
  project: ProjectDetailFragment
  initialGetTokenQueryVariables: GetProjectTokensQueryVariables
}

export const ProjectWorks = ({ project, initialGetTokenQueryVariables }: ProjectWorksProps): React.ReactElement => {
  // This should probably be up a level, but easier to implement here for now
  useEffect(() => {
    const handleHashChange = () => {
      if (window.location.hash !== '#artworks') {
        return
      }

      const container = document.getElementById('artworks')
      if (container) {
        container.scrollIntoView({ behavior: 'smooth' })
      }
    }

    window.addEventListener('hashchange', handleHashChange)
    handleHashChange()

    return () => {
      window.location.hash = ''
      window.removeEventListener('hashchange', handleHashChange)
    }
  }, [])

  const [searchParams] = useQueryStates(tokenFilterParsers)

  const {
    tokenQueryVariables,
    setPageNumber,
    pageNumber,
    featureFields,
    featureFieldsCounts,
    tokenFilter,
    setTokenFilter,
    sortOrder,
    sortOptions,
    setSortOrder,
    searchInvocationValue,
    handleSearchInvocationChange,
    clearSearchInvocationValue,
    searchInvocationSavedValues,
    handleSaveSearchInvocation,
    handleRemoveSearchInvocation,
    abListingsOnly,
    setABListingsOnly,
    walletAddressValue,
    handleWalletAddressChange,
    walletAddressSavedValues,
    handleSaveWalletAddress,
    handleRemoveWalletAddress,
    clearWalletAddressValue,
    ensAddresses,
    myArtworksOnly,
    handleMyArtworksOnlyChange
  } = useTokenFilters(project)

  const vars = {
    where: tokenQueryVariables.where,
    filter: tokenQueryVariables.filter,
    limit: tokenQueryVariables.limit,
    offset: tokenQueryVariables.offset,
    orderBy: tokenQueryVariables.orderBy,
    ttl: AVERAGE_SLOT_TIME
  }

  const [showListings, setShowListings] = useLocalStorage('showListings', false)

  const [openFilter, setOpenFilter] = useState(false)

  // When query variables match those used on the server, enable suspense for proper rehydration.
  // When filters change, disable suspense to prevent grid reset and allow smooth animation
  // of tokens entering/exiting as they match or fail to match the new filter criteria.
  const isUsingInitialTokenQueryVariables = useMemo(() => {
    return JSON.stringify(initialGetTokenQueryVariables) === JSON.stringify(tokenQueryVariables)
  }, [initialGetTokenQueryVariables, tokenQueryVariables])

  const projectId = project.id
  const projectActive = project.active
  const urqlOperationContext: Partial<OperationContext> = React.useMemo(
    () => ({
      suspense: isUsingInitialTokenQueryVariables,
      forceNoAuth: projectActive,
      ...createUrqlNextCacheContext({
        tags: [createTagWithVal(COLLECTIONS_REVALIDATE_TAG, projectId)],
        requestPolicy: projectActive ? 'cache-first' : 'cache-and-network'
      })
    }),
    [isUsingInitialTokenQueryVariables, projectId, projectActive]
  )

  const [tokenRes] = useQuery({
    query: GetProjectTokensDocument,
    variables: {
      ...vars
    },
    context: urqlOperationContext
  })

  const { data: tokenData, fetching: tokensFetching } = tokenRes

  const tokens = (tokenData?.filter_tokens_metadata_by_features ?? []).map((t) => ({ ...t, project }))
  const tokenCount = tokenData?.filter_tokens_metadata_by_features_aggregate?.aggregate?.count ?? 0
  const totalPages = tokenCount && tokenQueryVariables.limit ? Math.ceil(tokenCount / tokenQueryVariables.limit) : 1

  const clearFilters = () => {
    setTokenFilter(null)
    setABListingsOnly(false)
    clearSearchInvocationValue()
    clearWalletAddressValue()
  }

  const numberOfActiveFilters = getActiveFilterCount(
    tokenFilter,
    searchInvocationSavedValues,
    abListingsOnly,
    walletAddressSavedValues
  )

  // This is so messy I'm sorry, but it's the best way I found to prevent the grid from glitching
  // when switching between search and default grids
  const [showGrid, setShowGrid] = useState<ProjectGrids>(ProjectGrids.DEFAULT)
  useEffect(() => {
    setShowGrid((prevShowGrid) => {
      // Use search grid if search is active and loaded tokens are close to the saved search values
      if (
        (searchInvocationSavedValues.length > 0 && Math.abs(tokens.length - searchInvocationSavedValues.length) < 2) ||
        (walletAddressSavedValues.length > 0 && Math.abs(tokens.length - walletAddressSavedValues.length) < 2) ||
        myArtworksOnly
      ) {
        return ProjectGrids.SEARCH
      } else if (prevShowGrid === ProjectGrids.SEARCH && !searchInvocationSavedValues.length && tokens.length === 1) {
        // Temporarily disable grid when transitioning from search to default view
        return ProjectGrids.NONE
      } else {
        return ProjectGrids.DEFAULT
      }
    })
  }, [tokens.length, searchInvocationSavedValues.length, walletAddressSavedValues.length, myArtworksOnly])

  return (
    <div id="artworks" className="relative w-full">
      <Grid
        name={'project-works'}
        gridName={showGrid}
        fetching={tokensFetching}
        gridType={GridType.Tokens}
        sortOrder={sortOrder}
        sortOptions={sortOptions}
        onSortOrderChange={setSortOrder}
        onPageNumberChange={setPageNumber}
        pageNumber={pageNumber}
        totalPages={totalPages}
        numberOfActiveFilters={numberOfActiveFilters}
        onClearFilters={clearFilters}
        totalItems={tokenCount}
        gridItems={tokens}
        gridItemKey={(item) => `${item.token_id}`}
        gridItemComponent={(item) => (
          <ProjectTokenGridItem
            token={item}
            searchActive={showGrid === 'search'}
            showListing={showListings}
            aspectRatio={project.aspect_ratio}
            searchParams={searchParams}
          />
        )}
        filterLayout={
          <ProjectTokensFilter
            project={project}
            tokenCount={tokenCount}
            featureFields={featureFields}
            featureFieldsCounts={featureFieldsCounts}
            tokenFilter={tokenFilter}
            setTokenFilter={setTokenFilter}
            searchInvocationValue={searchInvocationValue}
            handleSearchInvocationChange={handleSearchInvocationChange}
            handleSaveSearchInvocation={handleSaveSearchInvocation}
            handleRemoveSearchInvocation={handleRemoveSearchInvocation}
            searchInvocationSavedValues={searchInvocationSavedValues}
            numActiveFilters={numberOfActiveFilters}
            filterOpen={openFilter}
            onFilterClose={() => setOpenFilter(false)}
            abListingsOnly={abListingsOnly}
            onABListingsOnlyChange={setABListingsOnly}
            walletAddressValue={walletAddressValue}
            handleWalletAddressChange={handleWalletAddressChange}
            walletAddressSavedValues={walletAddressSavedValues}
            handleSaveWalletAddress={handleSaveWalletAddress}
            handleRemoveWalletAddress={handleRemoveWalletAddress}
            clearWalletAddressValue={clearWalletAddressValue}
            ensAddresses={ensAddresses}
            myArtworksOnly={myArtworksOnly}
            handleMyArtworksOnlyChange={handleMyArtworksOnlyChange}
          />
        }
        additionalControls={
          <>
            {project.vertical_name?.toLowerCase() === 'curated' ? (
              <AllPurposeLink
                href={gridExperimentRoute(project.id)}
                className={cn(buttonVariants({ colorTheme: 'outline' }), 'hidden sm:block')}
                onClick={() => {
                  trackButtonClicked(TrackedButtonNames.exploreGrid, {
                    project
                  })
                }}
              >
                <BodyText>Explore GRID</BodyText>
              </AllPurposeLink>
            ) : null}
            <Button
              colorTheme={'outline'}
              onClick={() => {
                setShowListings(!showListings)
                trackButtonClicked(TrackedButtonNames.togglePrices, {
                  project,
                  customProperties: {
                    showListings: showListings
                  }
                })
              }}
            >
              <BodyText>{showListings ? 'Hide listings' : 'Show listings'}</BodyText>
            </Button>
          </>
        }
      />
    </div>
  )
}
